import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Styles from './AdsInsights.styles';
import useLocale from '@/hooks/useLocale';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { CHARLIE_ENABLE_MY_ADS_NAVIGATION, OPERACOES_ADS_INSIGHTS_CAMPAIGN_STATUS } from '@/config/GrowthBook/constants';
import {
  Button,
  IconAiStar,
  IconArrow,
  IconEngagement,
  IconAiMegaphone,
  IconAiRocket,
  IconStat,
  SocialMediaIcons,
} from 'gatorcomponents';
import { adsHandlerActions } from '@/redux/modules/adsHandler';
import { useChatbot } from '@/contexts/Chatbot';
import TrendingPost from './components/TrendingPost';
import AdsInsightsSkeleton from './components/AdsInsightsSkeleton';

const AdsInsights = ({
  goToMyAds,
  dateFrom,
  dateTo,
}) => {
  const dispatch = useDispatch();
  const { insights } = useSelector(state => state.adsHandler);
  const insightsData = insights.data;
  const insightsLoading = insights.loading;
  const { marketing: marketingLocale } = useLocale();
  const multiAgentScreensLocale = marketingLocale && marketingLocale.multiAgentScreens;
  const adsInsightsLocale = (multiAgentScreensLocale && multiAgentScreensLocale.adsInsights) || {};
  const enableMyAdsNavigation = useFeatureIsOn(CHARLIE_ENABLE_MY_ADS_NAVIGATION);
  const enableCampaignStatus = useFeatureIsOn(OPERACOES_ADS_INSIGHTS_CAMPAIGN_STATUS);

  useEffect(() => {
    if (dateFrom && dateTo) {
      dispatch(adsHandlerActions.insights.request({
        since: dateFrom,
        until: dateTo,
      }));
    }
  }, [dispatch, dateFrom, dateTo]);

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: adsInsightsLocale.statusDraft,
      pending: adsInsightsLocale.statusPending,
      active: adsInsightsLocale.statusActive,
      pending_billing_info: adsInsightsLocale.statusPendingBillingInfo,
      pending_review: adsInsightsLocale.statusPendingReview,
      paused: adsInsightsLocale.statusPaused,
      disapproved: adsInsightsLocale.statusDisapproved,
      archived: adsInsightsLocale.statusArchived,
      with_issues: adsInsightsLocale.statusWithIssues,
    };
    return statusLabels[status] || status;
  };

  const handleViewAllAds = () => {
    if (goToMyAds) {
      goToMyAds();
    }
  };

  const handleViewMyAds = () => {
    if (goToMyAds) {
      goToMyAds();
    }
  };

  const messages = insightsData && insightsData.messages ? insightsData.messages : 0;
  const totalEngagement = insightsData && insightsData.total_engagement ? insightsData.total_engagement : 0;
  const recentCampaigns = insightsData && insightsData.recents ? insightsData.recents : [];
  const highlightedPost = insightsData && insightsData.highlighted_post ? insightsData.highlighted_post : null;

  const {
    startAdsFlow,
    isActive,
    isReady,
  } = useChatbot();

  const isChatbotLoading = isActive && !isReady;

  // eslint-disable-next-line no-unused-vars
  const metaPost = useMemo(() => {
    if (!highlightedPost) return null;

    return {
      id: highlightedPost.id,
      image: highlightedPost.image || '',
      text: highlightedPost.text || '',
      likes: highlightedPost.likes || 0,
      comments: highlightedPost.comments || 0,
      shares: highlightedPost.shares || 0,
      date: highlightedPost.date || new Date().toISOString(),
      type: highlightedPost.type || 'photo',
      featured: true,
    };
  }, [highlightedPost]);


  // eslint-disable-next-line no-unused-vars
  const postStrategy = useMemo(() => {
    if (!highlightedPost || !highlightedPost.last_campaign) return null;

    const { target, budget } = highlightedPost.last_campaign;

    if (!target || !budget) return null;
    if (!target.genre || !target.location || !target.interests) return null;
    if (!Array.isArray(target.genre) || !Array.isArray(target.location) || !Array.isArray(target.interests)) return null;

    return {
      target: {
        genre: target.genre.map(g => (g === 'man' ? 'male' : 'female')),
        age: target.age || [18, 65],
        location: target.location.map(loc => ({
          key: loc.key,
          name: loc.name,
          type: loc.type,
          country_code: loc.country_code,
          country_name: loc.country_name,
          region_name: loc.region_name || null,
          region_id: loc.region_id || null,
        })),
        interests: target.interests.map(interest => ({
          id: interest.id,
          name: interest.name,
          audience_size_lower_bound: 0,
          audience_size_upper_bound: 1000,
        })),
      },
      budget: {
        amount: budget.amount,
        coin: budget.coin,
        frequency: budget.frequency,
        until: budget.until,
      },
      cta: {
        label: 'Saiba mais',
      },
    };
  }, [highlightedPost]);

  const handleStartAdsFlow = useCallback(() => {
    if (!isActive || !isReady) {
      return;
    }
    startAdsFlow(adsInsightsLocale.silentMessage);
  }, [isActive, isReady, startAdsFlow, adsInsightsLocale.silentMessage]);

  const handleOpenSurvey = () => {
    window.open('https://surveys.hotjar.com/ce960f67-5103-4eaa-a400-52c27ff1024b', '_blank', 'noopener,noreferrer');
  };

  const getStatusFromCampaign = (campaign) => {
    const statusStyleMap = {
      active: 'success',
      pending_billing_info: 'pending',
      pending_review: 'pending',
      paused: 'paused',
      disapproved: 'error',
      archived: 'archived',
      with_issues: 'error',
      draft: 'draft',
    };

    return statusStyleMap[campaign.status] || 'draft';
  };

  const activeAdsCount = recentCampaigns.filter(
    campaign => campaign.status === 'active',
  ).length;

  if (insightsLoading) {
    return <AdsInsightsSkeleton />;
  }

  return (
    <Styles.ContentWrapper>
      <Styles.SectionContainer $padding="40px 24px">
        <Styles.StatsContainer>
          <Styles.StatItem>
            <Styles.StatIcon>
              <IconStat color="lowDark" size="md" />
            </Styles.StatIcon>
            <Styles.StatContent>
              <Styles.StatTitleWrapper>
                <Styles.StatTitle>
                  {adsInsightsLocale.conversationsStarted}
                </Styles.StatTitle>
                <Styles.StatSubtitle>
                  {adsInsightsLocale.conversationsSubtitle}
                </Styles.StatSubtitle>
              </Styles.StatTitleWrapper>
              <Styles.StatValueRow>
                <Styles.StatValue>
                  {insightsLoading ? '-' : messages}
                </Styles.StatValue>
              </Styles.StatValueRow>
            </Styles.StatContent>
          </Styles.StatItem>

          <Styles.StatDivider />

          <Styles.StatItem>
            <Styles.StatIcon>
              <IconEngagement color="lowDark" size="md" />
            </Styles.StatIcon>
            <Styles.StatContent>
              <Styles.StatTitleWrapper>
                <Styles.StatTitle>
                  {adsInsightsLocale.totalEngagement}
                </Styles.StatTitle>
                <Styles.StatSubtitle>
                  {adsInsightsLocale.engagementSubtitle}
                </Styles.StatSubtitle>
              </Styles.StatTitleWrapper>
              <Styles.StatValueRow>
                <Styles.StatValue>
                  {insightsLoading ? '-' : totalEngagement}
                </Styles.StatValue>
              </Styles.StatValueRow>
            </Styles.StatContent>
          </Styles.StatItem>

          {enableMyAdsNavigation && (
            <Styles.StatsButtonWrapper>
              <Button
                iconLeft={<IconAiMegaphone color="highPure" />}
                label={adsInsightsLocale.viewMyAdsButton}
                size="large"
                notFillSVG
                onClick={handleViewMyAds}
              />
            </Styles.StatsButtonWrapper>
          )}
        </Styles.StatsContainer>

        {insightsData && insightsData.new_followers_percentage > 0 && (
          <Styles.InsightBanner>
            <Styles.InsightIcon>
              <IconAiStar gradient="secondary" size="sm2" />
            </Styles.InsightIcon>
            <Styles.InsightText>
              {adsInsightsLocale.followersInsightPrefix}
              <Styles.HighlightText>
                {`${adsInsightsLocale.followersInsightGrowth}${insightsData.new_followers_percentage}%`}
              </Styles.HighlightText>
              {adsInsightsLocale.followersInsightSuffix(insightsData.total_followers)}
            </Styles.InsightText>
          </Styles.InsightBanner>
        )}
      </Styles.SectionContainer>
      {highlightedPost && (
        <TrendingPost highlightedPost={highlightedPost} onBoostClick={handleStartAdsFlow} isLoading={isChatbotLoading} />
      )}
      <Styles.MainContentContainer>
        <Styles.SectionContainer>
          <Styles.RecentCampaignsContainer>
            <Styles.RecentCampaignsHeader>
              <Styles.RecentCampaignsTitle>
                {`${adsInsightsLocale.recentAdsTitle} - `}
                <Styles.HighlightText>
                  {`${activeAdsCount} ${adsInsightsLocale.activeLabel}`}
                </Styles.HighlightText>
              </Styles.RecentCampaignsTitle>
            </Styles.RecentCampaignsHeader>

            <Styles.CampaignsList>
              {insightsLoading ? (
                <Styles.StatValue>{adsInsightsLocale.loading}</Styles.StatValue>
              ) : (
                recentCampaigns.map((campaign, index) => {
                  const campaignStatus = getStatusFromCampaign(campaign);
                  return (
                    <React.Fragment key={campaign.id}>
                      {index > 0 && <Styles.CampaignDivider />}
                      <Styles.CampaignItem>
                        <Styles.CampaignThumbnail
                          src={campaign.image}
                          alt={campaign.post_id}
                        />
                        <Styles.CampaignInfo>
                          <Styles.CampaignTitle>
                            <SocialMediaIcons
                              icon={campaign.platform}
                              color="#5A5F66"
                              size="sm"
                            />
                            {adsInsightsLocale.boostedPostLabel}
                          </Styles.CampaignTitle>
                          <Styles.CampaignDescription>
                            {campaign.description}
                          </Styles.CampaignDescription>
                          {enableCampaignStatus && (
                            <Styles.CampaignStatus status={campaignStatus}>
                              {getStatusLabel(campaign.status)}
                            </Styles.CampaignStatus>
                          )}
                        </Styles.CampaignInfo>
                      </Styles.CampaignItem>
                    </React.Fragment>
                  );
                })
              )}
            </Styles.CampaignsList>

            {insightsData && insightsData.new_chats && insightsData.new_chats > 0 && (
              <Styles.InsightBanner>
                <Styles.InsightIcon>
                  <IconAiStar gradient="secondary" size="sm2" />
                </Styles.InsightIcon>
                <Styles.InsightText>
                  <strong>{adsInsightsLocale.engagementInsightTitle}</strong>
                  {' '}
                  {adsInsightsLocale.engagementInsightMessage(insightsData.new_chats)}
                </Styles.InsightText>
              </Styles.InsightBanner>
            )}

            {enableMyAdsNavigation && (
              <Styles.ViewAllButtonWrapper>
                <Button
                  label={adsInsightsLocale.viewAllAdsButton}
                  variant="secondary"
                  size="medium2"
                  iconRight={<IconArrow direction="right" />}
                  onClick={handleViewAllAds}
                />
              </Styles.ViewAllButtonWrapper>
            )}
          </Styles.RecentCampaignsContainer>
        </Styles.SectionContainer>

        <Styles.SideCardsContainer>
          <Styles.ActionCard onClick={!isChatbotLoading ? handleStartAdsFlow : undefined} $disabled={isChatbotLoading}>
            <Styles.ActionCardIcon>
              <IconAiRocket color="lowDark" size="md" />
            </Styles.ActionCardIcon>
            <Styles.ActionCardTitle>
              {adsInsightsLocale.boostPostTitle}
            </Styles.ActionCardTitle>
            <Styles.ActionCardDescription>
              {adsInsightsLocale.boostPostDescription}
            </Styles.ActionCardDescription>
          </Styles.ActionCard>

          <Styles.CardDivider />

          <Styles.ActionCard onClick={handleOpenSurvey}>
            <Styles.ComingSoonBadge>
              {adsInsightsLocale.comingSoonLabel}
            </Styles.ComingSoonBadge>
            <Styles.ActionCardIcon>
              <IconAiMegaphone color="lowDark" size="md" />
            </Styles.ActionCardIcon>
            <Styles.ActionCardTitle>
              {adsInsightsLocale.createCampaignTitle}
            </Styles.ActionCardTitle>
            <Styles.ActionCardDescription>
              {adsInsightsLocale.createCampaignDescription}
            </Styles.ActionCardDescription>
            <Styles.ActionCardInterestBox>
              <Styles.ActionCardInterestTitle>
                {adsInsightsLocale.showInterestTitle}
              </Styles.ActionCardInterestTitle>
              <Styles.ActionCardInterestMessage>
                {adsInsightsLocale.showInterestMessage}
              </Styles.ActionCardInterestMessage>
            </Styles.ActionCardInterestBox>
          </Styles.ActionCard>
        </Styles.SideCardsContainer>
      </Styles.MainContentContainer>
    </Styles.ContentWrapper>
  );
};

export default AdsInsights;
